import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  CASE_BUCKET_LABELS,
  CASE_BUCKETS,
  CASE_STATUSES,
  LOSS_TYPE_LABELS,
} from '@besideu/shared/cases';
import type { CaseBucket } from '@besideu/shared/cases';
import type { RosterResponse } from '@besideu/shared/users';
import { api } from '@/lib/api.ts';
import { useHrCases, HR_CASES_PAGE_SIZE as PAGE_SIZE } from '@/lib/use-hr-cases.ts';
import { useDebouncedValue } from '@/lib/use-debounced-value.ts';
import { StatusPill, StatusDot, ToneFor, humaniseStatus } from '@/components/ui/StatusPill.tsx';
import { Select } from '@/components/ui/Field.tsx';
import { Button, buttonClassName } from '@/components/ui/Button.tsx';

export function HrCases() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const managerId = searchParams.get('managerId') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const page = Number(searchParams.get('page') ?? '0');
  // Dashboard tile deep-link (`?bucket=`) — no select control of its own; a
  // stale/hand-edited value falls back to "no filter" rather than being sent
  // to the API and 400ing.
  const rawBucket = searchParams.get('bucket');
  const bucket: CaseBucket | '' =
    rawBucket && (CASE_BUCKETS as readonly string[]).includes(rawBucket)
      ? (rawBucket as CaseBucket)
      : '';

  const [qInput, setQInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(qInput, 300);
  // Tracks the last value THIS component pushed into the URL, so the resync
  // effect below can tell "the URL changed because we just typed" (no-op)
  // apart from "the URL changed from elsewhere" (browser back/forward,
  // another setSearchParams caller) — which must resync qInput/the visible
  // search box, or it silently disagrees with the URL and the results.
  const lastPushedQ = useRef(qParam);

  // Push the debounced search text into the URL (and reset to page 0) once
  // it settles — this is what actually drives the server-side fetch below.
  useEffect(() => {
    if (debouncedQ === qParam) return;
    lastPushedQ.current = debouncedQ;
    const sp = new URLSearchParams(searchParams);
    if (debouncedQ) sp.set('q', debouncedQ);
    else sp.delete('q');
    sp.delete('page');
    setSearchParams(sp, { replace: true });
    // Deliberately narrow deps: only react to the debounced value settling.
    // Including searchParams/qParam would loop against our own
    // setSearchParams call above.
  }, [debouncedQ]);

  // Resync the visible search box when `q` changes from a source OTHER than
  // the debounced push above (browser back/forward, or any other
  // setSearchParams caller) — otherwise the box can keep showing stale text
  // after the URL/results have already changed underneath it.
  useEffect(() => {
    if (qParam === lastPushedQ.current) return;
    lastPushedQ.current = qParam;
    setQInput(qParam);
  }, [qParam]);

  function setStatus(next: string) {
    const sp = new URLSearchParams(searchParams);
    if (!next) sp.delete('status');
    else sp.set('status', next);
    // A bucket already implies a status (`paused` = Quiet, the other three =
    // Open) — leaving both set risks a silently-empty, contradictory combo
    // (e.g. bucket=overdue + a manually-picked status=Closed). Manually
    // touching the status filter means the visitor has moved on from the
    // dashboard tile's deep link, so it's the natural point to drop it.
    sp.delete('bucket');
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  }
  function setManagerId(next: string) {
    const sp = new URLSearchParams(searchParams);
    if (!next) sp.delete('managerId');
    else sp.set('managerId', next);
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  }
  function clearBucket() {
    const sp = new URLSearchParams(searchParams);
    sp.delete('bucket');
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  }
  function setPage(next: number) {
    const sp = new URLSearchParams(searchParams);
    if (next === 0) sp.delete('page');
    else sp.set('page', String(next));
    setSearchParams(sp, { replace: true });
  }

  const cases = useHrCases({ q: qParam, status, managerId, bucket, page });

  // Dedicated, unpaginated roster for the manager filter — a manager not on
  // the current cases page must still be selectable. Unfiltered by status
  // (not just Active): a case can stay assigned to a manager after they're
  // deactivated (deactivating flags their open cases `Manager_Deactivated`
  // rather than reassigning them), so HR must still be able to filter to a
  // deactivated manager's remaining cases.
  const managerOptions = useQuery({
    queryKey: ['users', 'roster', 'Manager'],
    queryFn: () => api.get<RosterResponse>('/api/users/roster?role=Manager'),
  });

  const list = cases.data?.cases ?? [];
  const total = cases.data?.total ?? 0;
  const hasFilters = qParam !== '' || status !== '' || managerId !== '' || bucket !== '';

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Support pathways</h1>
        <p className="mt-2 text-muted-foreground">
          All bereavement support pathways across the organisation. Support pathways are opened by
          managers; HR sees everything but cannot read employee Maeve conversations.
        </p>
      </header>

      {/* Dashboard tile deep-link — no select control of its own (the bucket
          is a derived grouping, not a filter someone would pick from a
          list), so it needs its own visible "why is this filtered" pill with
          a way back out. */}
      {bucket ? (
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded-full bg-muted px-3 py-1 font-medium">
            {CASE_BUCKET_LABELS[bucket]}
          </span>
          <button
            type="button"
            onClick={clearBucket}
            className="text-muted-foreground underline-offset-4 hover:underline"
          >
            Clear filter
          </button>
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          placeholder="Search employee name"
          aria-label="Search employee name"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none sm:max-w-xs"
        />
        <Select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-auto py-2 text-sm"
        >
          <option value="">All statuses</option>
          {CASE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {humaniseStatus(s)}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by manager"
          value={managerId}
          onChange={(e) => setManagerId(e.target.value)}
          className="w-auto py-2 text-sm"
        >
          <option value="">All managers</option>
          {(managerOptions.data?.users ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.status === 'Active' ? m.name : `${m.name} (inactive)`}
            </option>
          ))}
        </Select>
        {/* Plain <a href> (not a fetch/Blob download) — the browser handles
            the file save natively off the Content-Disposition header, and
            same-origin httpOnly cookies attach automatically. Styled via
            buttonClassName since a native <button> can't carry an `href`.
            Carries the CURRENT filters so the export matches the table. */}
        <a
          href={`/api/hr/cases/export?${new URLSearchParams({
            ...(qParam ? { q: qParam } : {}),
            ...(status ? { status } : {}),
            ...(managerId ? { managerId } : {}),
            ...(bucket ? { bucket } : {}),
          })}`}
          className={buttonClassName('secondary')}
        >
          Export CSV
        </a>
      </div>

      {cases.isPending ? (
        <p className="text-muted-foreground">Loading support pathways…</p>
      ) : cases.error ? (
        <p className="text-traffic-red">Could not load support pathways.</p>
      ) : list.length === 0 ? (
        <EmptyState hasFilters={hasFilters} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <caption className="sr-only">
                Bereavement support pathways in this organisation
              </caption>
              <thead className="bg-muted/40 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Employee</th>
                  <th className="px-4 py-2 font-medium">Manager</th>
                  <th className="px-4 py-2 font-medium">Relationship</th>
                  <th className="px-4 py-2 font-medium">Type of loss</th>
                  <th className="px-4 py-2 font-medium">Loss</th>
                  {/* PO Spec §5 (2026-06-21) — privacy boundary. The
                      only employee-engagement signal HR sees is
                      "Portal opened: Yes/No". We do NOT render a
                      count, a timestamp, or "time on page". The
                      dot is a one-bit indicator; that is the
                      contract. */}
                  <th className="px-4 py-2 font-medium">Portal</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-2">
                      <span className="flex flex-wrap items-center gap-2">
                        <Link to={`/hr/cases/${c.id}`} className="font-medium hover:underline">
                          {c.employee.name}
                        </Link>
                        {c.hasOverdueFirstResponse ? <NoResponseBadge /> : null}
                        {c.hasOverdueTouchpoint ? <OverdueBadge /> : null}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{c.manager.name}</td>
                    <td className="px-4 py-2">{c.deceasedRelationship}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {c.lossType ? LOSS_TYPE_LABELS[c.lossType] : '—'}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{c.dateOfLoss}</td>
                    <td className="px-4 py-2">
                      <PortalOpenedIndicator value={c.portalOpened} />
                    </td>
                    <td className="px-4 py-2">
                      <StatusPill tone={ToneFor(c.status)}>{humaniseStatus(c.status)}</StatusPill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {total === 0
                ? 'No support pathways'
                : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button
                variant="secondary"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function OverdueBadge() {
  return (
    <StatusPill tone="danger" title="At least one touch point is more than 48 hours past due">
      Overdue
    </StatusPill>
  );
}

function NoResponseBadge() {
  return (
    <StatusPill
      tone="danger"
      title="Manager has not marked the first response sent within 24 hours of the support pathway being opened"
    >
      No response
    </StatusPill>
  );
}

// PO Spec §5 (2026-06-21) — privacy-boundary indicator. ONE bit
// (Yes/No) is the entire contract. The dot colour is the only
// visual difference; no count, no timestamp, no "time on page".
function PortalOpenedIndicator({ value }: { value: 'Yes' | 'No' }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
      title={value === 'Yes' ? 'Employee has opened their portal' : 'Portal not yet opened'}
    >
      <StatusDot tone={value === 'Yes' ? 'positive' : 'neutral'} />
      {value}
    </span>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/40 p-8 text-center">
      <p className="text-muted-foreground">
        {hasFilters
          ? 'No support pathways match the current search or filters.'
          : 'No support pathways yet. They appear here once a manager opens one.'}
      </p>
    </div>
  );
}
